import { derived, get, writable } from 'svelte/store';

import { DomainService } from '@client/services/domain';

import { appStore } from '@client/stores/app';
import { userStore } from '@client/stores/user';

/**
 * Store to track if domain switching is in progress
 * @type {import('svelte/store').Writable<boolean>}
 */
export const isDomainSwitching = writable(false);

/**
 * Derived store for the user's active domain.
 * @type {import('svelte/store').Readable<string>}
 */
export const activeDomain = derived(userStore, ($userStore) => $userStore.domain);

/**
 * Writable store to indicate if settings are being fetched.
 * @type {import('svelte/store').Writable<boolean>}
 */
const isFetching = writable(false);

// Track which domain is currently being fetched
let inFlightDomain = null;

// Domain-specific cache to avoid conflicts when switching domains
const domainSettingsCache = new Map();

/**
 * Helper function to check if the app state is initialized.
 * @returns {Promise<void>} - Returns a resolved promise if initialized property is true, otherwise waits for initialization.
 */
const checkAppState = () => {
  const initialized = get(appStore).initialized;

  if (initialized) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    const unsubscribe = appStore.subscribe(($appStore) => {
      if ($appStore.initialized) {
        unsubscribe();
        resolve();
      }
    });
  });
};

/**
 * Retrieves settings for the active domain.
 * @param {string} active - The currently active domain.
 * @param {string} last - The last managed domain.
 * @param {string} property - The specific property to check in the domain settings.
 * @param {Function} callbackFn - The callback function to execute with the retrieved settings.
 * @returns {Promise<boolean | undefined>} - Returns true if the property is enabled, false if not, or undefined if an error occurs.
 */
// Pending callbacks waiting for a fetch to complete, keyed by domain
const pendingCallbacks = new Map();

const getSettings = async (active, _last, property, callbackFn) => {
  if (!active) {
    callbackFn(false);
    return;
  }

  // Check domain-specific cache first
  const cachedSettings = domainSettingsCache.get(active);
  if (cachedSettings) {
    const value = cachedSettings[property] ?? false;
    callbackFn(value);
    return;
  }

  // No cache — if a fetch is already in progress for this domain, queue the callback.
  // Otherwise, start a new fetch for the requested domain.
  if (get(isFetching) && inFlightDomain === active) {
    if (!pendingCallbacks.has(active)) {
      pendingCallbacks.set(active, []);
    }
    pendingCallbacks.get(active).push({ property, callbackFn });
    return;
  }

  isFetching.set(true);
  inFlightDomain = active;
  lastDomainSeen.set(active);

  await checkAppState();

  const settings = await DomainService.getSettings();

  if (settings?.response >= 400) {
    callbackFn(false);
    const pending = pendingCallbacks.get(active);
    if (pending) {
      for (const { callbackFn: cb } of pending) {
        cb(false);
      }
      pendingCallbacks.delete(active);
    }
    inFlightDomain = null;
    isFetching.set(false);
    return;
  }

  // Cache settings for this specific domain
  domainSettingsCache.set(active, settings);

  const value = settings?.[property] ?? false;
  callbackFn(value);

  // Flush any pending callbacks that were waiting for this fetch
  const pending = pendingCallbacks.get(active);
  if (pending) {
    for (const { property: prop, callbackFn: cb } of pending) {
      const pendingValue = settings?.[prop] ?? false;
      cb(pendingValue);
    }
    pendingCallbacks.delete(active);
  }

  inFlightDomain = null;
  isFetching.set(false);
};

/**
 * Writable store for the user's last managed domain.
 * @type {import('svelte/store').Writable<string>}
 */
const lastDomainSeen = writable('');

/**
 * Creates a derived store for getting the actual value of a specific property from domain settings.
 * @param {string} property - The property to get from the domain settings.
 * @param {*} defaultValue - The default value to return if property is not found.
 * @returns {import('svelte/store').Readable<*>}
 */
const createPropertyValueStore = (property, defaultValue = null) => {
  const { subscribe, set } = writable(defaultValue);

  // Subscribe to domain changes and fetch settings
  const unsubscribe = derived(
    [activeDomain, lastDomainSeen, isDomainSwitching],
    ([$activeDomain, $lastDomainSeen, $isDomainSwitching]) => {
      return { activeDomain: $activeDomain, lastDomainSeen: $lastDomainSeen, isDomainSwitching: $isDomainSwitching };
    }
  ).subscribe(
    ({ activeDomain: $activeDomain, lastDomainSeen: $lastDomainSeen, isDomainSwitching: $isDomainSwitching }) => {
      // Don't fetch settings if domain switching is in progress
      if ($isDomainSwitching) {
        set(defaultValue); // Show default value while switching
        return;
      }

      // Reset to default value immediately when domain changes to prevent showing stale data
      if ($activeDomain !== $lastDomainSeen) {
        set(defaultValue);
      }

      // For value stores, we need to ensure settings are fetched and then get the value
      const cachedSettings = domainSettingsCache.get($activeDomain);

      if (cachedSettings) {
        // Cache exists, get the actual value
        const actualValue = cachedSettings[property] ?? defaultValue;
        set(actualValue);
      } else {
        // Need to fetch settings first, then get the value
        // Instead of competing with other getSettings calls, wait for any fetch to complete
        const checkForCache = () => {
          // No active domain (pre-domain-select / background init): nothing to fetch.
          // Returning here breaks the synchronous checkForCache <-> getSettings recursion
          // that otherwise overflows the stack (RangeError: Maximum call stack size exceeded).
          if (!$activeDomain) {
            set(defaultValue);
            return;
          }

          const newCachedSettings = domainSettingsCache.get($activeDomain);
          if (newCachedSettings) {
            const actualValue = newCachedSettings[property] ?? defaultValue;
            set(actualValue);
          } else {
            // Cache still not available, trigger a fetch if no one else is fetching
            if (!get(isFetching)) {
              getSettings($activeDomain, $lastDomainSeen, 'enablenewui', () => {
                checkForCache(); // Re-check after fetch completes
              });
            } else {
              // Someone else is fetching, wait a bit and check again
              setTimeout(checkForCache, 100);
            }
          }
        };

        checkForCache();
      }
    }
  );

  return {
    subscribe,
    destroy: unsubscribe
  };
};

/**
 * Creates a derived store for checking if a specific property is enabled in the domain settings.
 * @param {string} property - The property to check in the domain settings.
 * @returns {import('svelte/store').Readable<boolean>}
 */
const createPropertyCheckStore = (property) => {
  const { subscribe, set } = writable(false);

  // Subscribe to domain changes and fetch settings
  const unsubscribe = derived(
    [activeDomain, lastDomainSeen, isDomainSwitching],
    ([$activeDomain, $lastDomainSeen, $isDomainSwitching]) => {
      return { activeDomain: $activeDomain, lastDomainSeen: $lastDomainSeen, isDomainSwitching: $isDomainSwitching };
    }
  ).subscribe(
    ({ activeDomain: $activeDomain, lastDomainSeen: $lastDomainSeen, isDomainSwitching: $isDomainSwitching }) => {
      // Don't fetch settings if domain switching is in progress
      if ($isDomainSwitching) {
        set(false); // Show false while switching
        return;
      }

      // Reset to false immediately when domain changes to prevent showing stale data
      if ($activeDomain !== $lastDomainSeen) {
        set(false);
      }

      getSettings($activeDomain, $lastDomainSeen, property, (value) => {
        set(value);
      });
    }
  );

  return {
    subscribe,
    destroy: unsubscribe
  };
};

export const isGoogle = createPropertyCheckStore('googleenabled');
export const isMicrosoft = createPropertyCheckStore('office365enabled');
export const isGccHigh = createPropertyCheckStore('o365gcchigh');
export const isSalesforce = createPropertyCheckStore('salesforceconnectenabled');
export const isBambooHR = createPropertyCheckStore('bamboohrtoken');
export const newUIEnabled = createPropertyCheckStore('enablenewui');
export const billingId = createPropertyCheckStore('billing_id');
export const branding = createPropertyValueStore('branding', {});
export const defaultCampaignParams = createPropertyValueStore('bannerparams', []);
export const defaultCampaignTags = createPropertyValueStore('adtags', []);
export const internalDomains = createPropertyValueStore('internaldomains', []);
export const personas = createPropertyValueStore('personas', {});
export const isOutlookVisualPreview = derived(createPropertyValueStore('outlookaddin', null), (s) =>
  Boolean(s?.enableVisualPreview)
);
